import { useEffect, useState } from "react";
import { Link } from "@tanstack/react-router";
import { ShoppingBag, Minus, Plus, Trash2, ExternalLink, Loader2, X, Truck, Check } from "lucide-react";
import { useCartStore } from "@/stores/cartStore";
import { appendAffiliateRef } from "@/lib/affiliate";
import { CartPromoBar } from "@/components/CartPromoBar";
import { fetchProductsByCollection, type ShopifyProduct } from "@/lib/shopify";
import { Price } from "@/components/Price";

const FREE_SHIPPING_THRESHOLD = 75;

let hatIdsCache: Set<string> | null = null;

export function CartDrawer({ isOpen, onClose }: { isOpen: boolean; onClose: () => void }) {
  const { items, isLoading, isSyncing, updateQuantity, removeItem, getCheckoutUrl, syncCart, addItem } = useCartStore();
  const [hatIds, setHatIds] = useState<Set<string>>(hatIdsCache ?? new Set());
  const [recs, setRecs] = useState<ShopifyProduct[]>([]);
  const [recsLoading, setRecsLoading] = useState(false);
  const totalItems = items.reduce((sum, item) => sum + item.quantity, 0);
  const isHat = (item: typeof items[number]) =>
    hatIds.has(item.product.node.id) || (item.product.node.productType || '').toLowerCase() === 'hats';

  function formatOptionValue(item: typeof items[number], value: string): string {
    if (value === 'Default Title' && isHat(item)) return 'Premium hat';
    return value;
  }
  const hatCount = items.filter(isHat).reduce((sum, i) => sum + i.quantity, 0);
  const subtotal = items.reduce((sum, item) => sum + (parseFloat(item.price.amount) * item.quantity), 0);

  // B2G1: every 3rd hat is free (cheapest of the group). Expand hat units, sort ascending, free the lowest in each group of 3.
  const hatPrices: number[] = items
    .filter(isHat)
    .flatMap(i => Array(i.quantity).fill(parseFloat(i.price.amount)));
  hatPrices.sort((a, b) => a - b);
  const freeHatCount = Math.floor(hatPrices.length / 3);
  const b2g1Discount = hatPrices.slice(0, freeHatCount).reduce((sum, p) => sum + p, 0);
  const discountedTotal = Math.max(0, subtotal - b2g1Discount);

  const remainingForFreeShipping = Math.max(0, FREE_SHIPPING_THRESHOLD - discountedTotal);
  const progressPct = Math.min(100, (discountedTotal / FREE_SHIPPING_THRESHOLD) * 100);
  const qualified = discountedTotal >= FREE_SHIPPING_THRESHOLD && items.length > 0;


  useEffect(() => { if (isOpen) syncCart(); }, [isOpen, syncCart]);

  useEffect(() => {
    if (hatIdsCache) return;
    fetchProductsByCollection('all-hats', 100).then(products => {
      const ids = new Set(products.map(p => p.node.id));
      hatIdsCache = ids;
      setHatIds(ids);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    if (!isOpen || items.length > 0) return;
    setRecsLoading(true);
    Promise.all([
      fetchProductsByCollection('all-hats', 4).catch(() => [] as ShopifyProduct[]),
      fetchProductsByCollection('tops', 4).catch(() => [] as ShopifyProduct[]),
    ]).then(([hats, shirts]) => {
      const merged: ShopifyProduct[] = [];
      for (let i = 0; i < Math.max(hats.length, shirts.length); i++) {
        if (hats[i]) merged.push(hats[i]);
        if (shirts[i]) merged.push(shirts[i]);
      }
      setRecs(merged.slice(0, 8));
    }).finally(() => setRecsLoading(false));
  }, [isOpen, items.length]);

  const handleQuickAdd = async (product: ShopifyProduct) => {
    const variant = product.node.variants.edges[0]?.node;
    if (!variant || !variant.availableForSale) return;
    await addItem({
      product,
      variantId: variant.id,
      variantTitle: variant.title,
      price: variant.price,
      quantity: 1,
      selectedOptions: variant.selectedOptions || [],
    });
  };

  const handleCheckout = () => {
    const checkoutUrl = getCheckoutUrl();
    if (!checkoutUrl) return;

    // Defensive: persisted carts may have a checkoutUrl pointing at the
    // Lovable-hosted apex (coyotecreekhatco.com). Shopify checkout lives on
    // the checkout.coyotecreekhatco.com subdomain (CNAME -> shops.myshopify.com).
    let finalUrl = checkoutUrl;
    try {
      const u = new URL(checkoutUrl);
      if (u.hostname === 'coyotecreekhatco.com' || u.hostname === 'www.coyotecreekhatco.com') {
        u.hostname = 'checkout.coyotecreekhatco.com';
      }
      u.searchParams.set('channel', 'online_store');
      finalUrl = u.toString();
    } catch {
      /* keep the original checkout URL */
    }
    // BixGrow affiliate attribution: forward captured ?ref= to Shopify checkout
    // (BixGrow's Shopify app reads it there and credits the affiliate).
    finalUrl = appendAffiliateRef(finalUrl);
    window.open(finalUrl, '_blank');
    onClose();
  };

  return (
    <>
      {/* Overlay */}
      {isOpen && <div className="fixed inset-0 z-50 bg-black/50" onClick={onClose} />}

      {/* Drawer */}
      <div className={`fixed right-0 top-0 z-50 h-full w-[92vw] max-w-md transform bg-[#FDFAF3] shadow-2xl transition-transform duration-300 ${isOpen ? 'translate-x-0' : 'translate-x-full'}`}>
        <div className="flex h-full flex-col">
          {/* Header */}
          <div className="flex items-center justify-between border-b border-[#D4CCBC] px-6 py-4">
            <h2 className="font-heading text-xl font-bold uppercase tracking-wider text-[#2A1510]">
              Your Cart {totalItems > 0 && <span className="text-[#8B6F47]">({totalItems})</span>}
            </h2>
            <button onClick={onClose} aria-label="Close cart" className="text-[#8B6F47] hover:text-[#2A1510]">
              <X className="h-5 w-5" />
            </button>
          </div>

          {/* B2G1 Free hat promo */}
          <CartPromoBar hatCount={hatCount} />

          {/* Free shipping progress */}
          {items.length > 0 && (
            <div className="border-b border-[#D4CCBC] bg-[#F5EFE2] px-6 py-4">
              <div className="flex items-center gap-2">
                {qualified ? (
                  <Check className="h-4 w-4 text-[#FA5021]" />
                ) : (
                  <Truck className="h-4 w-4 text-[#FA5021]" />
                )}
                <p className="font-ui text-xs font-semibold text-[#2A1510]">
                  {qualified
                    ? "You qualify for FREE shipping!"
                    : <>You're <Price amount={remainingForFreeShipping} className="font-bold text-[#FA5021]" /> away from FREE shipping</>
                  }
                </p>
              </div>
              <div className="mt-2 h-1.5 w-full overflow-hidden rounded-full bg-[#D4CCBC]">
                <div
                  className="h-full bg-[#FA5021] transition-all duration-500"
                  style={{ width: `${progressPct}%` }}
                />
              </div>
            </div>
          )}

          {/* Items */}
          {items.length === 0 ? (
            <div className="flex flex-1 flex-col overflow-y-auto">
              {/* Empty message */}
              <div className="flex flex-col items-center justify-center gap-4 px-6 py-10">
                <ShoppingBag className="h-12 w-12 text-[#8B6F47]" />
                <p className="font-heading text-lg font-bold uppercase tracking-wider text-[#2A1510]">Your cart is empty</p>
                <button
                  onClick={onClose}
                  className="btn-cc-accent tracking-[0.2em]"
                >
                  Continue Shopping
                </button>
              </div>

              {/* Recommendations */}
              <div className="px-6 pb-8">
                <h3 className="mb-4 font-heading text-sm font-bold uppercase tracking-wider text-[#2A1510]">Popular Picks</h3>
                {recsLoading ? (
                  <div className="grid grid-cols-2 gap-3">
                    {Array.from({ length: 4 }).map((_, i) => (
                      <div key={i} className="animate-pulse space-y-2">
                        <div className="aspect-square rounded-sm bg-[#F5EFE2]" />
                        <div className="h-3 w-2/3 rounded bg-[#F5EFE2]" />
                        <div className="h-3 w-1/2 rounded bg-[#F5EFE2]" />
                      </div>
                    ))}
                  </div>
                ) : recs.length > 0 ? (
                  <div className="grid grid-cols-2 gap-3">
                    {recs.map((product) => {
                      const variant = product.node.variants.edges[0]?.node;
                      const image = product.node.images.edges[0]?.node;
                      const price = product.node.priceRange.minVariantPrice;
                      const soldOut = !variant || !variant.availableForSale;
                      return (
                        <div key={product.node.id} className="flex flex-col rounded-sm border border-[#D4CCBC] bg-[#F5EFE2] p-2">
                          <Link to="/products/$handle" params={{ handle: product.node.handle }} className="relative aspect-square overflow-hidden rounded-sm bg-[#FDFAF3]">
                            {image ? (
                              <img src={image.url} alt={image.altText || product.node.title} className="h-full w-full object-cover" loading="lazy" />
                            ) : (
                              <div className="flex h-full items-center justify-center">
                                <span className="font-ui text-[10px] text-[#8B6F47]">No image</span>
                              </div>
                            )}
                          </Link>
                          <div className="mt-2 min-w-0">
                            <Link to="/products/$handle" params={{ handle: product.node.handle }}>
                              <h4 className="truncate font-heading text-[11px] font-bold uppercase text-[#2A1510]">{product.node.title}</h4>
                            </Link>
                            <p className="mt-0.5 font-body text-xs font-bold text-[#8B6F47]"><Price amount={price.amount} /></p>
                          </div>
                          <button
                            onClick={() => handleQuickAdd(product)}
                            disabled={isLoading || soldOut}
                            className="mt-2 flex w-full items-center justify-center rounded-sm bg-[#2A1510] py-1.5 font-ui text-[10px] font-bold uppercase tracking-wider text-[#F5EFE2] transition-all hover:bg-[#FA5021] disabled:opacity-50"
                          >
                            {isLoading ? <Loader2 className="h-3 w-3 animate-spin" /> : soldOut ? "Sold Out" : "Quick Add"}
                          </button>
                        </div>
                      );
                    })}
                  </div>
                ) : null}
              </div>
            </div>
          ) : (
            <>
              <div className="flex-1 overflow-y-auto px-6 py-4">
                <div className="space-y-3">
                  {items.map((item) => (
                    <div key={item.variantId} className="flex gap-4 rounded-sm border border-[#D4CCBC] bg-[#F5EFE2] p-3">
                      <div className="h-20 w-20 flex-shrink-0 overflow-hidden rounded-sm bg-[#FDFAF3]">
                        {item.product.node.images?.edges?.[0]?.node && (
                          <img src={item.product.node.images.edges[0].node.url} alt={item.product.node.title} className="h-full w-full object-cover" />
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <h4 className="truncate font-heading text-sm font-bold uppercase text-[#2A1510]">{item.product.node.title}</h4>
                        <p className="font-ui text-[11px] uppercase tracking-wider text-[#8B6F47]">{item.selectedOptions.map(o => formatOptionValue(item, o.value)).join(' / ')}</p>
                        <p className="mt-1 font-body text-sm font-bold text-[#2A1510]"><Price amount={item.price.amount} /></p>
                      </div>
                      <div className="flex flex-col items-end justify-between flex-shrink-0">
                        <button onClick={() => removeItem(item.variantId)} aria-label="Remove" className="text-[#8B6F47] hover:text-[#FA5021]">
                          <Trash2 className="h-4 w-4" />
                        </button>
                        <div className="flex items-center gap-1 rounded-sm border border-[#D4CCBC] bg-[#FDFAF3]">
                          <button onClick={() => updateQuantity(item.variantId, item.quantity - 1)} aria-label="Decrease" className="flex h-7 w-7 items-center justify-center text-[#2A1510] hover:bg-[#F5EFE2]">
                            <Minus className="h-3 w-3" />
                          </button>
                          <span className="w-6 text-center font-ui text-xs font-bold text-[#2A1510]">{item.quantity}</span>
                          <button onClick={() => updateQuantity(item.variantId, item.quantity + 1)} aria-label="Increase" className="flex h-7 w-7 items-center justify-center text-[#2A1510] hover:bg-[#F5EFE2]">
                            <Plus className="h-3 w-3" />
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Footer */}
              <div className="space-y-2 border-t border-[#D4CCBC] bg-[#F5EFE2] px-6 py-4">
                <div className="flex justify-between font-ui text-sm text-[#2A1510]">
                  <span>Subtotal</span>
                  <Price amount={subtotal} className="font-bold" />
                </div>
                {b2g1Discount > 0 && (
                  <div className="flex justify-between font-ui text-xs text-[#2E5A3D]">
                    <span className="font-bold uppercase tracking-wider">Buy 2 Get 1 Free ({freeHatCount} free hat{freeHatCount > 1 ? 's' : ''})</span>
                    <Price amount={b2g1Discount} negative className="font-bold" />
                  </div>
                )}
                <div className="flex justify-between font-ui text-xs text-[#8B6F47]">
                  <span>Shipping</span>
                  <span>{qualified ? "FREE" : "Calculated at checkout"}</span>
                </div>
                <div className="flex justify-between border-t border-[#D4CCBC] pt-2 font-ui text-sm text-[#2A1510]">
                  <span className="font-bold uppercase tracking-wider">Estimated total</span>
                  <Price amount={discountedTotal} className="font-bold" />
                </div>

                <button
                  onClick={handleCheckout}
                  disabled={items.length === 0 || isLoading || isSyncing}
                  className="w-full btn-cc-accent tracking-[0.2em]"
                >
                  {isLoading || isSyncing ? <Loader2 className="h-4 w-4 animate-spin" /> : <><ExternalLink className="h-4 w-4" /> Checkout</>}
                </button>
                <p className="text-center font-ui text-[10px] uppercase tracking-wider text-[#8B6F47]">
                  Secure checkout powered by Shopify
                </p>
              </div>
            </>
          )}
        </div>
      </div>
    </>
  );
}
